import { Skeleton } from './skeleton';
import { Card, CardContent, CardHeader } from './card';

// Email List Skeleton with shimmer effect
export function EmailListSkeleton() {
  return (
    <div className="px-2 py-1 space-y-1">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="p-2 rounded-md">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Skeleton className="h-6 w-6 rounded-full animate-shimmer" />
              <Skeleton className="h-3 w-24 animate-shimmer" />
            </div>
            <Skeleton className="h-3 w-12 animate-shimmer" />
          </div>
          <Skeleton className="h-3 w-full mb-1 animate-shimmer" />
          <Skeleton className="h-2 w-3/4 animate-shimmer" />
          <div className="flex items-center gap-1 mt-1">
            <Skeleton className="h-4 w-12 animate-shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Dashboard Stats Skeleton with shimmer effect
export function DashboardStatsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <Skeleton className="h-4 w-24 animate-shimmer" />
            <Skeleton className="h-4 w-4 rounded animate-shimmer" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-16 mb-2 animate-shimmer" />
            <Skeleton className="h-1 w-full mb-1 animate-shimmer" />
            <Skeleton className="h-3 w-24 animate-shimmer" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Email Intelligence Feed Skeleton
export function EmailFeedSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="p-3 bg-background/50 rounded-lg border border-border/50">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div>
                <Skeleton className="h-3 w-32 mb-1" />
                <Skeleton className="h-2 w-48" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-12 rounded" />
            </div>
          </div>
          <Skeleton className="h-2 w-full mb-1" />
          <Skeleton className="h-2 w-3/4 mb-2" />
          <Skeleton className="h-2 w-24" />
        </div>
      ))}
    </div>
  );
}

// Contact Intelligence Skeleton
export function ContactIntelligenceSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="p-3 bg-background/50 rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-3 w-24 mb-1" />
              <Skeleton className="h-2 w-32" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-2 w-16" />
              <Skeleton className="h-4 w-12 rounded" />
            </div>
            <Skeleton className="h-1 w-full" />
            <div className="flex gap-1">
              <Skeleton className="h-4 w-16 rounded" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Task List Skeleton
export function TaskListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Skeleton className="h-5 w-5 mt-1 rounded" />
              <div className="flex-1 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded" />
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// AI Chat Skeleton
export function AIChatSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
          <div className={`max-w-[80%] ${i % 2 === 0 ? 'bg-accent/50' : 'bg-primary/10'} p-4 rounded-lg`}>
            <Skeleton className="h-3 w-full mb-2" />
            <Skeleton className="h-3 w-3/4 mb-2" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Email Detail Skeleton
export function EmailDetailSkeleton() {
  return (
    <div className="flex-1 flex flex-col">
      <div className="border-b border-border/20 px-6 py-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          <div className="flex items-center gap-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-8 rounded" />
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 px-6 py-4">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-20 w-full rounded-md" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
}

// Settings Section Skeleton
export function SettingsSectionSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-48 mb-2" />
            <Skeleton className="h-3 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-6 w-12 rounded-full" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-6 w-12 rounded-full" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Calendar Skeleton
export function CalendarSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded" />
          <Skeleton className="h-9 w-24 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 35 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded" />
        ))}
      </div>
    </div>
  );
}

// Top 50 Dashboard Skeleton - Fast loading for Top-50 contacts
export function Top50DashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-11 w-44 rounded" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12 mb-2" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-border">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-32 rounded-t" />
          ))}
        </div>

        {/* Contact List Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-96" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <Card key={i} className="relative">
                  <CardContent className="pt-6 pl-20">
                    <div className="absolute top-4 left-4">
                      <Skeleton className="w-10 h-10 rounded-full" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <Skeleton className="h-5 w-48" />
                          </div>
                          <div className="flex gap-2 mb-3">
                            <Skeleton className="h-5 w-16 rounded" />
                            <Skeleton className="h-5 w-24 rounded" />
                          </div>
                          <div className="grid grid-cols-5 gap-2 mt-4">
                            {Array.from({ length: 5 }).map((_, j) => (
                              <div key={j}>
                                <Skeleton className="h-3 w-16 mb-1" />
                                <Skeleton className="h-2 w-full mb-1" />
                                <Skeleton className="h-3 w-8" />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="text-center ml-4">
                          <Skeleton className="h-6 w-16 rounded mb-2" />
                          <Skeleton className="h-10 w-12 mb-1" />
                          <Skeleton className="h-3 w-12" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Top 50 Single Contact Card Skeleton (for lazy loading)
export function Top50ContactCardSkeleton() {
  return (
    <Card className="relative">
      <CardContent className="pt-6 pl-20">
        <div className="absolute top-4 left-4">
          <Skeleton className="w-10 h-10 rounded-full" />
        </div>
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-5 w-48" />
              </div>
              <div className="flex gap-2 mb-3">
                <Skeleton className="h-5 w-16 rounded" />
                <Skeleton className="h-5 w-24 rounded" />
              </div>
              <div className="grid grid-cols-5 gap-2 mt-4">
                {Array.from({ length: 5 }).map((_, j) => (
                  <div key={j}>
                    <Skeleton className="h-3 w-16 mb-1" />
                    <Skeleton className="h-2 w-full mb-1" />
                    <Skeleton className="h-3 w-8" />
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center ml-4">
              <Skeleton className="h-6 w-16 rounded mb-2" />
              <Skeleton className="h-10 w-12 mb-1" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
